import EmblaCarousel, {EmblaCarouselType, EmblaOptionsType} from 'embla-carousel'
import {WheelGesturesPlugin} from 'embla-carousel-wheel-gestures'

export interface SliderComponent {
  embla: EmblaCarouselType | null;
  init(wrapper: HTMLElement, options: EmblaOptionsType, isThumbs: boolean): void;
  addTogglePrevNextBtnsActive(
    prevBtn: HTMLElement,
    nextBtn: HTMLElement
  ): () => void;
  addPrevNextBtnsClickHandlers(
    prevBtn: HTMLElement,
    nextBtn: HTMLElement
  ): () => void;
  addDotBtnsAndClickHandlers(
    dots: HTMLElement
  ): () => void;

  scrollTo(index: number): void;
}

export default {
  name: 'slider',
  component(): SliderComponent {
    return {
      embla: null,

      init(wrapper: HTMLElement, options: EmblaOptionsType, isThumbs: boolean) {
        if (!wrapper) return

        const emblaNode = wrapper.querySelector<HTMLElement>('.embla__viewport')
        if (!emblaNode) return

        let plugins = [];

        if (isThumbs) {
          plugins = [WheelGesturesPlugin()]
        }

        this.embla = EmblaCarousel(emblaNode, options, plugins)

        const prevBtnNode = wrapper.querySelector<HTMLElement>('.embla__button--prev')
        const nextBtnNode = wrapper.querySelector<HTMLElement>('.embla__button--next')

        if (prevBtnNode && nextBtnNode) {
          const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
            prevBtnNode,
            nextBtnNode
          )
          this.embla.on('destroy', removePrevNextBtnsClickHandlers)
        }

        const dots = wrapper.querySelector<HTMLElement>('.embla__dots')
        if (dots) {
          const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(
            dots
          )
          this.embla.on('destroy', removeDotBtnsAndClickHandlers)
        }
      },

      addTogglePrevNextBtnsActive(prevBtn, nextBtn) {
        const togglePrevNextBtnsState = () => {
          prevBtn.toggleAttribute('disabled', !this.embla.canScrollPrev())
          nextBtn.toggleAttribute('disabled', !this.embla.canScrollNext())
        }

        this.embla
          .on('select', togglePrevNextBtnsState)
          .on('init', togglePrevNextBtnsState)
          .on('reInit', togglePrevNextBtnsState)

        return () => {
          prevBtn.removeAttribute('disabled')
          nextBtn.removeAttribute('disabled')
        }
      },

      addPrevNextBtnsClickHandlers(prevBtn, nextBtn) {
        const scrollPrev = () => this.embla.scrollPrev()
        const scrollNext = () => this.embla.scrollNext()

        prevBtn.addEventListener('click', scrollPrev)
        nextBtn.addEventListener('click', scrollNext)

        const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
          prevBtn,
          nextBtn
        )

        return () => {
          removeTogglePrevNextBtnsActive()
          prevBtn.removeEventListener('click', scrollPrev)
          nextBtn.removeEventListener('click', scrollNext)
        }
      },

      addDotBtnsAndClickHandlers(dots) {
        let dotNodes: HTMLElement[] = []

        const addDotBtnsWithClickHandlers = () => {
          dots.innerHTML = this.embla
            .scrollSnapList()
            .map(() => '<button class="embla__dot" type="button"></button>')
            .join('')

          dotNodes = Array.from(dots.querySelectorAll<HTMLElement>('.embla__dot'))
          dotNodes.forEach((dotNode, index) => {
            dotNode.addEventListener('click', () => this.embla.scrollTo(index))
          })
        }

        const toggleDotBtnsActive = () => {
          const previous = this.embla.previousScrollSnap()
          const selected = this.embla.selectedScrollSnap()
          dotNodes[previous]?.classList.remove('embla__dot--selected')
          dotNodes[selected]?.classList.add('embla__dot--selected')
        }

        this.embla
          .on('init', addDotBtnsWithClickHandlers)
          .on('reInit', addDotBtnsWithClickHandlers)
          .on('init', toggleDotBtnsActive)
          .on('reInit', toggleDotBtnsActive)
          .on('select', toggleDotBtnsActive)

        return () => {
          dots.innerHTML = ''
        }
      },
      scrollTo(index: number) {
        this.embla?.scrollTo(index)
      },
    }
  },
}
